import { connectionAtom } from "@/atoms/settings"
import { TOKEN_PROGRAM_ID } from "@/utils/constants"
import { CustomConnection } from "@/utils/on_chain/custom-connection"
import { GetProgramAccountsFilter, PublicKey } from "@solana/web3.js"
import { useAtomValue } from "jotai"
import { useCallback } from "react"
export type TokenAccountInfo = {
  isSucceeded: boolean
  accountData?: any // You might want to specify a more specific type here
  mintAddress?: string
  tokenBalance?: number
}

const useCheckingOnChain = () => {
  const connection = useAtomValue(connectionAtom)

  const getTokenAccountInfo = async (
    publicKey: PublicKey,
    tokenPubkey: PublicKey,
    abortController?: AbortController,
  ) => {
    try {
      const filters: GetProgramAccountsFilter[] = [
        {
          dataSize: 165, //size of account (bytes)
        },
        {
          memcmp: {
            offset: 32, //location of our query in the account (bytes)
            bytes: publicKey.toBase58(), //our search criteria, a base58 encoded string
          },
        },
        {
          memcmp: {
            offset: 0, //number of bytes
            bytes: tokenPubkey.toBase58(), //base58 encoded string
          },
        },
      ]

      const customConnection = new CustomConnection(connection)
      const accounts = await customConnection.getParsedProgramAccountsWithAbortSignal(
        TOKEN_PROGRAM_ID,
        {
          filters: filters,
        },
        abortController,
      )

      if (accounts.length > 0) {
        const parsedAccountInfo: any = accounts[0].account.data
        const mintAddress: string = parsedAccountInfo["parsed"]["info"]["mint"]
        const tokenBalance: number = parsedAccountInfo["parsed"]["info"]["tokenAmount"]["uiAmount"]

        return {
          isSucceeded: true,
          accountData: parsedAccountInfo,
          mintAddress: mintAddress,
          tokenBalance: tokenBalance,
        }
      }

      console.log("Account not found")
      return {
        isSucceeded: false,
        accountData: undefined,
        mintAddress: undefined,
        tokenBalance: undefined,
      }
    } catch (error) {
      console.error(error)
      return {
        isSucceeded: false,
        accountData: undefined,
        mintAddress: undefined,
        tokenBalance: undefined,
      }
    }
  }

  const getSPLTokenBalance = useCallback(
    async (tokenAccount: PublicKey) => {
      try {
        const tokenAccountInfo = await connection.getTokenAccountBalance(tokenAccount)
        return tokenAccountInfo.value.uiAmount ?? 0
      } catch (error) {
        console.error("Error checking SPL token balance:", error)
        return 0
      }
    },
    [connection],
  )

  return { getTokenAccountInfo, getSPLTokenBalance }
}

export default useCheckingOnChain
